import { Dialog, DialogTitle } from 'components/Dialog';
import { Box } from '@mui/material';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { closeUnlessEscape } from 'utils/closeUnlessEscape';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useId, useState } from 'react';
import { usePolls } from 'features/polls/usePolls';
import QuizFormContainer from 'features/quizzes/components/quiz-form/QuizFormContainer';
import { editQuizRequested } from 'features/quizzes/actions';
import type { CreateQuizData } from 'features/quizzes/types';
import { selectQuizById } from 'features/quizzes/quizzesSlice';

const EditQuizModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const quizId = modalData.id as string;

  const { isFeatureAvailable, canManagePolls } = usePolls();

  const quiz = useAppSelector((state) => selectQuizById(state, quizId));

  const [quizData, setQuizData] = useState<CreateQuizData>({
    title: quiz?.title || '',
    timeLimitSeconds: quiz?.timeLimitSeconds || 0,
    timingMode: quiz?.timingMode || 'quiz',
    showCorrectAnswers: quiz?.showCorrectAnswers || false,
    passingScorePercentage: quiz?.passingScorePercentage,
    questions:
      quiz?.questions.map((q) => ({
        text: q.text,
        multipleChoices: q.multipleChoices,
        choices: q.choices.map((c) => ({
          text: c.text,
          correct: c.correct || false,
        })),
      })) || [],
  });

  const modalShouldBeClosed = !isFeatureAvailable || !canManagePolls || !quiz?.editable;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('editQuiz'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const handleFormChange = (data: CreateQuizData) => {
    setQuizData(data);
  };

  const handleSubmit = (data: CreateQuizData) => {
    dispatch(
      editQuizRequested({
        id: quizId,
        title: data.title,
        timeLimitSeconds: data.timeLimitSeconds,
        timingMode: data.timingMode,
        showCorrectAnswers: data.showCorrectAnswers,
        passingScorePercentage: data.passingScorePercentage,
        questions: data.questions,
      })
    );
    dispatch(closeModal('editQuiz'));
  };

  const handleCancel = () => {
    dispatch(closeModal('editQuiz'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      fullWidth
      aria-labelledby={`${modalId}-title`}
      onClose={closeUnlessEscape(handleCancel)}
      disableRestoreFocus
    >
      <DialogTitle id={`${modalId}-title`}>{t('quizzes.edit_quiz.title')}</DialogTitle>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <QuizFormContainer
          quizData={quizData}
          onChange={handleFormChange}
          onDialogClose={handleCancel}
          onDialogSubmit={handleSubmit}
          editForm
        />
      </Box>
    </Dialog>
  );
};

export default EditQuizModal;
